import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ticketApi } from "../../api/ticket";
import "../../style.css";

/**
 * CheckoutStart - Seamless Mobile-to-Web Authentication Handler
 *
 * TICKETING METHODOLOGY:
 * This component eliminates the UX friction of manual login by automatically
 * authenticating users who arrive from the mobile app via purchase sessions.
 * The goal is zero-click authentication for ticket purchases.
 *
 * SECURITY ARCHITECTURE:
 * - Receives session ID via secure POST form (not URL parameters)
 * - Validates session with backend API to prevent tampering
 * - Exchanges session for standard JWT tokens (access + refresh)
 * - Stores tokens in localStorage following existing web app patterns
 * - Session is consumed and destroyed after single use
 *
 * USER EXPERIENCE FLOW:
 * 1. Mobile user taps "Purchase Tickets" → Creates session
 * 2. Mobile opens browser with HTML form → Auto-submits session data
 * 3. This component validates session → Gets JWT tokens automatically
 * 4. Sets user in auth context → Redirects to ticket purchase page
 * 5. User completes purchase (no manual login required!)
 *
 * ERROR HANDLING:
 * - Session expired: Clear message with return-to-app option
 * - Session invalid: User-friendly error with retry guidance
 * - Network errors: Graceful degradation with helpful messaging
 *
 * MVP IMPLEMENTATION:
 * Focuses on core authentication flow with professional error handling.
 * Future enhancements (session extension, multiple event support, analytics)
 * are documented for post-MVP implementation.
 *
 * REACT STRICTMODE HANDLING:
 * Uses ref to prevent duplicate session validation calls when React StrictMode
 * double-invokes effects in development. Backend detects race conditions gracefully.
 *
 * @see /Users/bradkile/Documents/Apps/projects/vc/CLAUDE.md - Overall system architecture
 * @see /Users/bradkile/Documents/Apps/projects/vc/dance-locale-web/CLAUDE.md - Web-specific enhancements
 */
const CheckoutStart = () => {
    const { login } = useAuth();
    const navigate = useNavigate();

    const [status, setStatus] = useState("initializing");
    const [error, setError] = useState("");

    // Prevent duplicate session validation (React StrictMode double-invokes effects)
    const sessionValidatedRef = useRef(false);

    useEffect(() => {
        const handleSessionAuth = async () => {
            // Prevent duplicate API calls (React StrictMode double-invokes effects)
            if (sessionValidatedRef.current) {
                return;
            }

            try {
                sessionValidatedRef.current = true;
                setStatus("processing");

                // Extract session data from URL parameters (POST form data becomes URL params in React)
                const urlParams = new URLSearchParams(window.location.search);
                const sessionId = urlParams.get("sessionId");
                const eventId = urlParams.get("eventId");
                const returnApp = urlParams.get("returnApp");

                // Detect dev mode implicitly from session ID prefix (security: no devMode flag in URL)
                const isDevSession = sessionId?.startsWith("ps_dev_");

                // CRITICAL: Set dev mode BEFORE any API calls so axios interceptor adds the header
                if (isDevSession) {
                    localStorage.setItem('dance-locale-dev-mode', 'true');
                } else {
                    localStorage.removeItem('dance-locale-dev-mode');
                }

                if (!sessionId || !eventId) {
                    setError("Invalid session. Please try purchasing tickets again.");
                    setStatus("error");
                    return;
                }

                // Validate and exchange session for user authentication
                // Now axios interceptor will add X-App-Version header if dev mode is enabled
                const sessionResponse = await ticketApi.validateSession(sessionId);

                if (!sessionResponse.ok || !sessionResponse.data.success) {
                    let errorMessage = "Your session has expired. Please try again.";
                    if (sessionResponse.status === 404) {
                        errorMessage = "Invalid session. Please try purchasing tickets again.";
                    } else if (sessionResponse.status === 409) {
                        errorMessage = "This session has already been used.";
                    } else if (sessionResponse.status === 410) {
                        errorMessage = "Your session has expired. Please try again.";
                    }

                    setError(errorMessage);
                    setStatus("error");
                    return;
                }

                const { accessToken, refreshToken, user } = sessionResponse.data.session;

                // Store JWT tokens in localStorage (same as regular login)
                localStorage.setItem("accessToken", accessToken);
                localStorage.setItem("refreshToken", refreshToken);

                // Set user in auth context (same as successful login)
                login(user);

                // Redirect directly to ticket purchase page (user is now authenticated!)
                // Preserve devMode parameter if this is a dev session
                const devModeParam = isDevSession ? '&devMode=true' : '';
                const redirectUrl = `/tickets?eventId=${eventId}&returnApp=${returnApp || "mobile"}${devModeParam}`;

                setStatus("redirecting");
                navigate(redirectUrl);

            } catch (error) {
                console.error("CheckoutStart - Error:", error);

                let errorMessage = "An error occurred during authentication. Please try again.";
                if (error.response?.status === 401) {
                    errorMessage = "Authentication failed. Please try purchasing tickets again.";
                } else if (error.response?.status >= 500) {
                    errorMessage = "Server error. Please try again later.";
                }
                
                setError(errorMessage);
                setStatus("error");
            }
        };

        // Check if we have POST form data or URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const hasSessionData = urlParams.get("sessionId") || urlParams.get("eventId");

        if (hasSessionData) {
            handleSessionAuth();
        } else {
            // No session data - might be direct navigation
            setError("No session data found. Please use the mobile app to purchase tickets.");
            setStatus("error");
        }
    }, [login, navigate]);

    const handleRetry = () => {
        window.location.href = "dancelocale://tickets/cancelled";
    };

    const handleGoHome = () => {
        navigate("/");
    };

    // Render loading state
    if (status === "initializing" || status === "processing" || status === "redirecting") {
        return (
            <div className="checkout-start-container">
                <div className="checkout-start-content">
                    <div className="checkout-start-spinner-wrap">
                        <div className="checkout-start-spinner"></div>
                    </div>
                    <h2 className="loading-title">
                        {status === "initializing" && "Initializing..."}
                        {status === "processing" && "Authenticating..."}
                        {status === "redirecting" && "Opening Ticket Purchase..."}
                    </h2>
                    <p className="loading-message">
                        {status === "initializing" && "Setting up your secure session"}
                        {status === "processing" && "Validating your session and authenticating automatically"}
                        {status === "redirecting" && "Authentication successful! Taking you to ticket purchase"}
                    </p>
                </div>
                <style jsx>{`
                    .checkout-start-container {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #451f78, #10bfbf);
                        color: white;
                        text-align: center;
                        padding: 2rem;
                    }
                    
                    .checkout-start-content {
                        max-width: 400px;
                        width: 100%;
                    }
                    
                    .checkout-start-container .checkout-start-spinner-wrap {
                        margin-bottom: 2rem;
                    }

                    /* White-on-turquoise spinner. Scoped under the container so no global
                       .spinner rule (e.g. Common.css's purple one) can override the colour —
                       this project has no styled-jsx, so <style jsx> is injected as plain
                       global CSS and would otherwise depend on cascade order. */
                    .checkout-start-container .checkout-start-spinner {
                        box-sizing: border-box;
                        width: 40px;
                        height: 40px;
                        border: 3px solid rgba(255,255,255,0.3);
                        border-top-color: #fff;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin: 0 auto;
                    }
                    
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    .loading-title {
                        font-size: 1.5rem;
                        font-weight: 600;
                        margin-bottom: 1rem;
                    }
                    
                    .loading-message {
                        font-size: 1rem;
                        opacity: 0.9;
                        line-height: 1.5;
                    }
                    
                    .error-container {
                        max-width: 400px;
                        width: 100%;
                        text-align: center;
                    }
                    
                    .error-icon {
                        font-size: 3rem;
                        margin-bottom: 1rem;
                    }
                    
                    .error-title {
                        font-size: 1.5rem;
                        font-weight: 600;
                        margin-bottom: 1rem;
                    }
                    
                    .error-message {
                        font-size: 1rem;
                        margin-bottom: 2rem;
                        line-height: 1.5;
                        opacity: 0.9;
                    }
                    
                    .error-buttons {
                        display: flex;
                        gap: 1rem;
                        justify-content: center;
                        flex-wrap: wrap;
                    }
                    
                    .error-button {
                        padding: 0.75rem 1.5rem;
                        border: 2px solid rgba(255,255,255,0.3);
                        background: rgba(255,255,255,0.1);
                        color: white;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 0.9rem;
                        font-weight: 500;
                        transition: all 0.2s ease;
                        text-decoration: none;
                        display: inline-block;
                    }
                    
                    .error-button:hover {
                        background: rgba(255,255,255,0.2);
                        border-color: rgba(255,255,255,0.5);
                        transform: translateY(-2px);
                    }
                    
                    .error-button.primary {
                        background: rgba(255,255,255,0.2);
                        border-color: rgba(255,255,255,0.5);
                    }
                `}</style>
            </div>
        );
    }

    // Render error state
    if (status === "error") {
        return (
            <div className="checkout-start-container">
                <div className="error-container">
                    <div className="error-icon">⚠️</div>
                    <h2 className="error-title">Authentication Error</h2>
                    <p className="error-message">{error}</p>
                    <div className="error-buttons">
                        <button onClick={handleRetry} className="error-button primary">
                            Return to App
                        </button>
                        <button onClick={handleGoHome} className="error-button">
                            Go to Home
                        </button>
                    </div>
                </div>
                <style jsx>{`
                    .checkout-start-container {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #451f78, #10bfbf);
                        color: white;
                        text-align: center;
                        padding: 2rem;
                    }
                    
                    .error-container {
                        max-width: 400px;
                        width: 100%;
                        text-align: center;
                    }
                    
                    .error-icon {
                        font-size: 3rem;
                        margin-bottom: 1rem;
                    }
                    
                    .error-title {
                        font-size: 1.5rem;
                        font-weight: 600;
                        margin-bottom: 1rem;
                    }
                    
                    .error-message {
                        font-size: 1rem;
                        margin-bottom: 2rem;
                        line-height: 1.5;
                        opacity: 0.9;
                    }
                    
                    .error-buttons {
                        display: flex;
                        gap: 1rem;
                        justify-content: center;
                        flex-wrap: wrap;
                    }
                    
                    .error-button {
                        padding: 0.75rem 1.5rem;
                        border: 2px solid rgba(255,255,255,0.3);
                        background: rgba(255,255,255,0.1);
                        color: white;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 0.9rem;
                        font-weight: 500;
                        transition: all 0.2s ease;
                        text-decoration: none;
                        display: inline-block;
                    }
                    
                    .error-button:hover {
                        background: rgba(255,255,255,0.2);
                        border-color: rgba(255,255,255,0.5);
                        transform: translateY(-2px);
                    }
                    
                    .error-button.primary {
                        background: rgba(255,255,255,0.2);
                        border-color: rgba(255,255,255,0.5);
                    }
                `}</style>
            </div>
        );
    }

    return null;
};

export default CheckoutStart;